import React, { useEffect, useRef, useState } from 'react';
import { Edit3, GalleryVerticalEnd, ImagePlus, Images, Loader2, Plus, Search, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

import { collectionsAPI, mediaAPI } from '../lib/api';
import CloudinaryMediaLibraryDialog from '../components/CloudinaryMediaLibraryDialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';


const blankCollection = {
  name: '',
  description_html: '',
  seo_title: '',
  seo_keywords: '',
  seo_meta_description: '',
  slug: '',
  banner_image: null,
  is_active: true,
  visibility: 'public',
};

const previewUrl = (url) => url?.replace(
  '/image/upload/',
  '/image/upload/f_auto,q_auto:eco,c_fill,g_auto,w_1000,h_420/',
);

const slugify = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .trim()
  .replace(/[\s_-]+/g, '-');

const errorMessage = (error, fallback) => error.response?.data?.detail || error.message || fallback;

export default function Collections() {
  const uploadRef = useRef(null);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankCollection);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await collectionsAPI.getAll();
      setCollections(response.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load collections'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCollections(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankCollection);
    setSlugTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (collection) => {
    setEditingId(collection.id);
    setForm({
      name: collection.name || '',
      description_html: collection.description_html || '',
      seo_title: collection.seo_title || '',
      seo_keywords: collection.seo_keywords || '',
      seo_meta_description: collection.seo_meta_description || '',
      slug: collection.slug || '',
      banner_image: collection.banner_image || null,
      is_active: collection.is_active !== false,
      visibility: collection.visibility || 'public',
    });
    setSlugTouched(true);
    setDialogOpen(true);
  };

  const updateName = (name) => setForm((current) => ({
    ...current,
    name,
    slug: slugTouched ? current.slug : slugify(name),
    seo_title: current.seo_title || name,
  }));

  const applyBannerAsset = (asset, message = 'selected') => {
    setForm((current) => ({
      ...current,
      banner_image: {
        ...asset,
        alt: asset?.alt || `${current.name || 'URBNSW'} collection banner`,
      },
    }));
    toast.success(`Collection banner ${message}`);
  };

  const uploadBanner = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const asset = await mediaAPI.uploadFile(file);
      applyBannerAsset(asset, 'uploaded to Cloudinary');
    } catch (error) {
      toast.error(errorMessage(error, 'Banner upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const saveCollection = async () => {
    if (!form.name.trim()) return toast.error('Collection name is required');
    if (!form.slug.trim()) return toast.error('URL slug is required');
    if (!form.banner_image?.url) return toast.error('Upload a collection banner image');
    try {
      setSaving(true);
      if (editingId) await collectionsAPI.update(editingId, form);
      else await collectionsAPI.create(form);
      toast.success(editingId ? 'Collection updated across ERP and storefront' : 'Collection created');
      setDialogOpen(false);
      await loadCollections();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save collection'));
    } finally {
      setSaving(false);
    }
  };

  const deleteCollection = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await collectionsAPI.delete(deleteTarget.id);
      toast.success('Collection deleted');
      setDeleteTarget(null);
      await loadCollections();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to delete collection'));
    } finally {
      setSaving(false);
    }
  };

  const visible = collections.filter((item) => (
    `${item.name} ${item.slug}`.toLowerCase().includes(query.toLowerCase())
  ));

  return (
    <div className="space-y-6" data-testid="collections-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Collection Management</h1>
          <p className="mt-1 text-slate-500">Manage product drops, collection banners, storefront content, and SEO.</p>
        </div>
        <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New Collection</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search collections" className="pl-9" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex h-52 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-500" /></div>
      ) : visible.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-16 text-center text-slate-500">
          <GalleryVerticalEnd className="mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-700">No collections found</p>
          <p className="text-sm">Create the first product collection for your storefront.</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((collection) => (
            <Card key={collection.id} className="overflow-hidden">
              <div className="relative aspect-[12/5] bg-slate-100">
                {collection.banner_image?.url ? (
                  <img src={previewUrl(collection.banner_image.url)} alt={collection.banner_image.alt || collection.name} className="h-full w-full object-cover" />
                ) : <div className="flex h-full items-center justify-center text-slate-400"><ImagePlus /></div>}
                <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.35)_0.8px,transparent_0.8px)] bg-[length:4px_4px]" />
              </div>
              <CardContent className="space-y-3 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div><h2 className="font-semibold text-slate-900">{collection.name}</h2><p className="text-xs text-slate-500">/collections/{collection.slug}</p></div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Badge variant={collection.visibility === 'private' ? 'secondary' : 'default'}>{collection.visibility === 'private' ? 'Private' : 'Public'}</Badge>
                    <Badge variant={collection.is_active === false ? 'secondary' : 'outline'}>{collection.is_active === false ? 'Inactive' : 'Active'}</Badge>
                  </div>
                </div>
                <p className="text-sm text-slate-600">{collection.product_count || 0} assigned product{collection.product_count === 1 ? '' : 's'}</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => openEdit(collection)}><Edit3 className="mr-2 h-4 w-4" />Edit</Button>
                  <Button variant="outline" size="icon" className="text-red-600" onClick={() => setDeleteTarget(collection)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => !saving && setDialogOpen(open)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Collection' : 'Create Collection'}</DialogTitle>
            <DialogDescription>Content is sanitized before it is published. Use H2/H3 headings, paragraphs, lists, strong and links.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 py-2 md:grid-cols-2">
            <div className="space-y-2"><Label>Collection name *</Label><Input value={form.name} onChange={(event) => updateName(event.target.value)} placeholder="Next Era" /></div>
            <div className="space-y-2"><Label>URL slug *</Label><Input value={form.slug} onChange={(event) => { setSlugTouched(true); setForm({ ...form, slug: slugify(event.target.value) }); }} placeholder="next-era" /><p className="text-xs text-slate-500">Public URL: /collections/{form.slug || 'collection-name'}</p></div>
            <div className="space-y-2 md:col-span-2"><Label>HTML collection description</Label><Textarea value={form.description_html} onChange={(event) => setForm({ ...form, description_html: event.target.value })} rows={10} className="font-mono text-xs" placeholder={'<h2>Premium streetwear collection</h2>\n<p>Discover our latest <strong>URBNSW</strong> drop.</p>'} /></div>
            <div className="space-y-2"><Label>SEO title</Label><Input value={form.seo_title} maxLength={70} onChange={(event) => setForm({ ...form, seo_title: event.target.value })} /><p className="text-right text-xs text-slate-400">{form.seo_title.length}/70</p></div>
            <div className="space-y-2"><Label>SEO keywords</Label><Input value={form.seo_keywords} onChange={(event) => setForm({ ...form, seo_keywords: event.target.value })} placeholder="streetwear, Sri Lanka, premium t-shirts" /></div>
            <div className="space-y-2 md:col-span-2"><Label>SEO meta description</Label><Textarea value={form.seo_meta_description} maxLength={180} onChange={(event) => setForm({ ...form, seo_meta_description: event.target.value })} rows={3} /><p className="text-right text-xs text-slate-400">{form.seo_meta_description.length}/180</p></div>
            <div className="space-y-3 md:col-span-2">
              <Label>Collection banner *</Label>
              <input ref={uploadRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" className="hidden" onChange={uploadBanner} />
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {form.banner_image?.url ? <img src={previewUrl(form.banner_image.url)} alt={form.banner_image.alt || form.name} className="aspect-[12/4] w-full object-cover" /> : <div className="flex aspect-[12/4] items-center justify-center text-slate-400"><ImagePlus className="mr-2" />No banner uploaded</div>}
                <div className="flex flex-col gap-3 border-t bg-white p-3 sm:flex-row sm:items-end">
                  {form.banner_image?.url ? <div className="flex-1 space-y-1"><Label className="text-xs">Banner alt text</Label><Input value={form.banner_image.alt || ''} onChange={(event) => setForm({ ...form, banner_image: { ...form.banner_image, alt: event.target.value } })} /></div> : <div className="flex-1 text-sm text-slate-500">Recommended: 2400 × 1000 px, WebP or AVIF.</div>}
                  <Button type="button" variant="outline" onClick={() => setLibraryOpen(true)} disabled={uploading}><Images className="mr-2 h-4 w-4" />Choose existing</Button>
                  <Button type="button" variant="outline" onClick={() => uploadRef.current?.click()} disabled={uploading}>{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}{form.banner_image ? 'Upload replacement' : 'Upload new'}</Button>
                  {form.banner_image ? <Button type="button" variant="outline" className="text-red-600" onClick={() => setForm({ ...form, banner_image: null })}><Trash2 className="h-4 w-4" /></Button> : null}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Storefront visibility</Label>
              <Select value={form.visibility || 'public'} onValueChange={(value) => setForm({ ...form, visibility: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public — show on storefront</SelectItem>
                  <SelectItem value="private">Private — ERP only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Private collections, landing pages, filters, products assigned to them, and sitemap entries are excluded from the storefront.</p>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} /><div><Label>Active for ERP use</Label><p className="text-xs text-slate-500">Inactive records remain stored but are unavailable for normal product assignment.</p></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button><Button onClick={saveCollection} disabled={saving || uploading}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingId ? 'Update Collection' : 'Create Collection'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CloudinaryMediaLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        title="Choose collection banner from Cloudinary"
        onSelect={(asset) => applyBannerAsset(asset, 'selected from Cloudinary')}
      />

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete {deleteTarget?.name}?</DialogTitle><DialogDescription>A collection assigned to products cannot be deleted. Reassign those products through GRN first.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="destructive" onClick={deleteCollection} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Delete Collection</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
